import React, { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  ArrowRight,
  ArrowDown,
  Play,
  Clapperboard,
  Users,
  Briefcase,
  Megaphone,
  Award,
  Film,
  Pizza,
  Upload,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { guildAuthUrl } from '@/lib/guildUrl'

import kinoraLogo from '@shared-assets/kinora_logo.svg'

/* ─────────────────────────────────────────────
   DATA
   ───────────────────────────────────────────── */

const CAPABILITIES = [
  {
    icon: <Users className="w-6 h-6" />,
    title: 'Your craft profile',
    description:
      'Build a profile around what you actually do — 60 skills across 12 production departments, from directing to color grading.',
  },
  {
    icon: <Briefcase className="w-6 h-6" />,
    title: 'Find work',
    description:
      'Browse the open-roles board and apply to crew calls on real productions — with compensation terms up front.',
  },
  {
    icon: <Megaphone className="w-6 h-6" />,
    title: 'Post your production',
    description:
      'Recruiting for your own project? Post roles, review applications, and message applicants directly.',
  },
  {
    icon: <Award className="w-6 h-6" />,
    title: 'Verified credits',
    description:
      'Accepted crew get credits attached to real Kinora releases — a portfolio that proves itself.',
  },
]

const STEPS = [
  {
    step: '01',
    icon: <Users className="w-6 h-6" />,
    title: 'Create your profile',
    description:
      'Same account as Kinora. Pick your crafts, set your availability, add portfolio pieces and links.',
  },
  {
    step: '02',
    icon: <Clapperboard className="w-6 h-6" />,
    title: 'Crew up',
    description:
      'Apply to open roles on other productions — or post your own project and build the crew you need.',
  },
  {
    step: '03',
    icon: <Film className="w-6 h-6" />,
    title: 'Ship it',
    description:
      'When the work is released on Kinora, verified crew credits appear on the watch page — with your name on them.',
  },
]

const DEPARTMENTS = [
  'Directing',
  'Writing',
  'Camera',
  'Lighting',
  'Editing',
  'Sound',
  'Score & Music',
  'Acting',
  'Animation',
  'Production',
  'Art & Design',
  'VFX',
]

/* ─────────────────────────────────────────────
   COMPONENTS (same visual language as Landing)
   ───────────────────────────────────────────── */

/** Staggered fade-in on scroll */
const FadeIn = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          obs.unobserve(el)
        }
      },
      { threshold: 0.1 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.6s cubic-bezier(.16,1,.3,1) ${delay}ms, transform 0.6s cubic-bezier(.16,1,.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  )
}

/* ─────────────────────────────────────────────
   MAIN PAGE
   ───────────────────────────────────────────── */

const Guild = () => {
  const navigate = useNavigate()

  return (
    <div className="bg-background text-foreground min-h-screen overflow-x-hidden">
      {/* ── Inline keyframes & utilities (same as Landing) ── */}
      <style>{`
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .text-gradient {
          background: linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / .7) 50%, hsl(var(--accent, var(--primary))) 100%);
          background-size: 200% 200%;
          animation: gradient-shift 6s ease infinite;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .glow-card:hover {
          box-shadow: 0 0 40px -12px hsl(var(--primary) / .25);
        }
      `}</style>

      {/* ════════════════════════════════════════
          HERO
         ════════════════════════════════════════ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-primary/10 blur-[120px]" />
          <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] rounded-full bg-primary/5 blur-[100px]" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-28">
          <FadeIn className="flex flex-col items-center text-center gap-6">
            <div className="flex flex-col items-center gap-3 md:gap-4 max-w-4xl">
              <img
                src={kinoraLogo}
                alt="Kinora"
                className="h-10 md:h-12 w-auto drop-shadow-lg select-none"
                draggable={false}
              />
              <p className="text-xs font-bold text-primary/70 uppercase tracking-[0.25em]">
                The Kinora Guild
              </p>
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.1] text-balance">
                Where indie crews{' '}
                <span className="text-gradient whitespace-nowrap">are made.</span>
              </h1>
            </div>

            <p className="text-base md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              The Guild is Kinora's creator network — build your craft profile,
              find your crew, and get credited on real releases.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <Button
                size="lg"
                className="group text-base px-8 h-12"
                onClick={() => window.open(guildAuthUrl(), '_blank', 'noopener,noreferrer')}
              >
                Create your guild profile
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="group text-base px-8 h-12"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              >
                See how it works
                <ArrowDown className="w-4 h-4 ml-2 group-hover:translate-y-0.5 transition-transform" />
              </Button>
            </div>

            <p className="text-xs text-muted-foreground mt-1">
              Already watching on Kinora? The same account works in the Guild.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ════════════════════════════════════════
          WHAT YOU CAN DO
         ════════════════════════════════════════ */}
      <section className="border-y border-border/30 bg-card/30 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-10 md:py-14">
          <FadeIn className="text-center mb-10">
            <h2 className="text-2xl md:text-4xl font-bold tracking-tight">
              Everything a production needs
            </h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
              Whether you're looking for work or looking for people — the Guild
              is where Kinora creators find each other.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {CAPABILITIES.map((cap, i) => (
              <FadeIn key={cap.title} delay={i * 120}>
                <Card className="h-full bg-card/60 backdrop-blur-sm border-border/40 hover:border-primary/30 transition-all duration-300 glow-card">
                  <CardContent className="p-6">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 text-primary mb-4">
                      {cap.icon}
                    </div>
                    <h3 className="text-base font-bold mb-2">{cap.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {cap.description}
                    </p>
                  </CardContent>
                </Card>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          HOW IT WORKS
         ════════════════════════════════════════ */}
      <section id="how-it-works" className="relative scroll-mt-20">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.03] to-transparent pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 py-10 md:py-14">
          <FadeIn className="text-center mb-10">
            <h2 className="text-2xl md:text-4xl font-bold tracking-tight">
              How the Guild works
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {STEPS.map((step, i) => (
              <FadeIn key={step.step} delay={i * 150}>
                <div className="relative group">
                  {i < STEPS.length - 1 && (
                    <div className="hidden md:block absolute top-10 left-[calc(50%+40px)] w-[calc(100%-40px)] border-t border-dashed border-border/40" />
                  )}
                  <Card className="bg-card/60 backdrop-blur-sm border-border/40 hover:border-primary/30 transition-all duration-300 glow-card">
                    <CardContent className="p-6 md:p-8 text-center">
                      <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 text-primary mb-4 group-hover:scale-110 transition-transform duration-300">
                        {step.icon}
                      </div>
                      <div className="text-xs font-bold text-primary/60 uppercase tracking-widest mb-2">
                        Step {step.step}
                      </div>
                      <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          THE ECOSYSTEM — watch → make → publish
         ════════════════════════════════════════ */}
      <section className="max-w-6xl mx-auto px-6 py-10 md:py-14">
        <FadeIn className="text-center mb-10">
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight">
            One account.{' '}
            <span className="text-gradient">The whole ecosystem.</span>
          </h2>
          <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
            Kinora isn't just a streaming service — it's where indie work gets
            funded, made, and released. Your account moves with you.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {[
            {
              icon: <Play className="w-6 h-6" />,
              title: 'Kinora',
              tagline: 'Watch & fund it',
              description:
                'Stream indie films and series, donate your monthly slices, and back crowdfunding campaigns.',
              cta: 'Start watching',
              onClick: () => navigate('/browse'),
            },
            {
              icon: <Clapperboard className="w-6 h-6" />,
              title: 'Kinora Guild',
              tagline: 'Make it',
              description:
                'You are here. Crew up, find collaborators, and earn verified credits on real productions.',
              cta: 'Join the Guild',
              onClick: () => window.open(guildAuthUrl(), '_blank', 'noopener,noreferrer'),
              current: true,
            },
            {
              icon: <Upload className="w-6 h-6" />,
              title: 'Kinora Studio',
              tagline: 'Publish it',
              description:
                'When the project is ready, found a studio straight from the Guild and publish to the Kinora catalogue.',
              cta: 'Start publishing',
              onClick: () => window.open('https://publish.slicetv.app', '_blank', 'noopener,noreferrer'),
            },
          ].map((app, i) => (
            <FadeIn key={app.title} delay={i * 120}>
              <Card
                className={`relative h-full border glow-card transition-all duration-300 hover:-translate-y-1 ${
                  app.current
                    ? 'border-primary/50 bg-card/80'
                    : 'border-border/40 bg-card/50'
                }`}
              >
                <CardContent className="p-6 md:p-8 flex flex-col h-full">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 text-primary mb-4">
                    {app.icon}
                  </div>
                  <p className="text-xs font-bold text-primary/60 uppercase tracking-widest">
                    {app.tagline}
                  </p>
                  <h3 className="text-lg font-bold mt-1 mb-2">{app.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                    {app.description}
                  </p>
                  <Button
                    variant={app.current ? 'default' : 'outline'}
                    className="mt-6 w-full h-11 group"
                    onClick={app.onClick}
                  >
                    {app.cta}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ════════════════════════════════════════
          WHO IT'S FOR
         ════════════════════════════════════════ */}
      <section className="max-w-4xl mx-auto px-6 py-10 md:py-14">
        <FadeIn className="text-center mb-8">
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight">
            Whatever your craft
          </h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
            Twelve production departments, sixty skills — if indie film needs it,
            there's a place for it here.
          </p>
        </FadeIn>

        <FadeIn delay={100}>
          <div className="flex flex-wrap justify-center gap-3">
            {DEPARTMENTS.map((d) => (
              <span
                key={d}
                className="inline-flex items-center px-4 py-1.5 rounded-full border border-border/50 bg-card/40 text-sm text-muted-foreground whitespace-nowrap backdrop-blur-sm"
              >
                {d}
              </span>
            ))}
          </div>
        </FadeIn>
      </section>

      {/* ════════════════════════════════════════
          FINAL CTA
         ════════════════════════════════════════ */}
      <section className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.04] to-primary/[0.08] pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 py-12 md:py-16">
          <FadeIn className="text-center">
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight max-w-2xl mx-auto leading-tight">
              Your next project starts with{' '}
              <span className="text-gradient">your crew.</span>
            </h2>
            <p className="text-muted-foreground mt-4 max-w-md mx-auto text-base md:text-lg">
              Join the Kinora Guild and start building the team that builds the thing.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              <Button
                size="lg"
                className="group text-base px-10 h-12"
                onClick={() => window.open(guildAuthUrl(), '_blank', 'noopener,noreferrer')}
              >
                Create your guild profile
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-base px-10 h-12"
                onClick={() => navigate('/')}
              >
                <Pizza className="w-5 h-5 mr-2" />
                Back to Kinora
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Already watching on Kinora? The same account works in the Guild.
            </p>
          </FadeIn>
        </div>
      </section>
    </div>
  )
}

export default Guild
